import { useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useMutation } from "@tanstack/react-query";

import { SubmitButton } from "ui-components/Form";
import { Layout } from "ui-components/Layout";
import { Success, Error_ } from "ui-components/Alert";
import { useCustomerValue } from "providers/CustomerContext";
import { useCustWhSettingQuery, useVendorOptions, useSKUOptions } from "hooks/useQueryHook";
import { formRender, initialValues, vendorSKUValidation } from "./vendor-sku-schema";
import { createVendorSKU } from "services/data.internal.service";
import VendorSKUInfo from "./components/VendorSKUInfo";
import { useLocation, useNavigate } from "react-router-dom";

function AddVendorSKU() {
  const { customerCode } = useCustomerValue();
  const location = useLocation();
  const navigate = useNavigate();
  const { skuCode = null } = location?.state ?? {};
  const [alert, setAlert] = useState(null);
  const [formValues, setFormValues] = useState({ ...initialValues, customerCode, skuCode, vendorSKU:skuCode });

  const [custSetting, setCustSetting] = useState({});
  const [custVendor, setCustVendor] = useState({});

  const [vendorOptions, setVendorOptions] = useState([]);
  const [skuOptions, setSKUOptions] = useState([]);
  const [auto, setAuto] = useState(false);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const createVendorSKUMutation = useMutation(createVendorSKU, {
    onSuccess: (data) => setAlert(<Success data={data} onChange={setAlert} />),
    onError,
  });
  const form = useForm({
    defaultValues: initialValues,
    values: formValues,
    resolver: yupResolver(vendorSKUValidation),
  });

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    reset,
  } = form;

  const [
    defRecvWh,
    defRecvZone,
    defRecvLocation,
    defPutawayWh,
    defPutawayZone,
    defPutawayLocation,
    defTRPutawayWh,
    defTRPutawayZone,
    defTRPutawayLocation,
    defShipWh,
    defShipZone,
    defShipLocation,
  ] = watch([
    "defRecvWh",
    "defRecvZone",
    "defRecvLocation",
    "defPutawayWh",
    "defPutawayZone",
    "defPutawayLocation",
    "defTRPutawayWh",
    "defTRPutawayZone",
    "defTRPutawayLocation",
    "defShipWh",
    "defShipZone",
    "defShipLocation",
  ]);

  useCustWhSettingQuery({
    customerCode,
    onSuccess: (data) => {
      if (data.data.data === undefined) {
        return;
      }
      const {
        defRecvWh,
        defRecvZone,
        defRecvLocation,
        defPutawayWh,
        defPutawayZone,
        defPutawayLocation,
        defTRPutawayWh,
        defTRPutawayZone,
        defTRPutawayLocation,
        defShipWh,
        defShipZone,
        defShipLocation,
        autoCreation,
      } = data.data.data;

      setValue("defRecvWh", defRecvWh);
      setValue("defRecvZone", defRecvZone);
      setValue("defRecvLocation", defRecvLocation);
      setValue("defPutawayWh", defPutawayWh);
      setValue("defPutawayZone", defPutawayZone);
      setValue("defPutawayLocation", defPutawayLocation);
      setValue("defTRPutawayWh", defTRPutawayWh);
      setValue("defTRPutawayZone", defTRPutawayZone);
      setValue("defTRPutawayLocation", defTRPutawayLocation);
      setValue("defShipWh", defShipWh);
      setValue("defShipZone", defShipZone);
      setValue("defShipLocation", defShipLocation);
      setCustSetting({
        defRecvWh,
        defRecvZone,
        defRecvLocation,
        defPutawayWh,
        defPutawayZone,
        defPutawayLocation,
        defTRPutawayWh,
        defTRPutawayZone,
        defTRPutawayLocation,
        defShipWh,
        defShipZone,
        defShipLocation,
      });
      setAuto(autoCreation);
    },
    onError,
  });
  useVendorOptions({
    customerCode,
    onSuccess: (data) => {
      const vendors = data.data.data;

      setVendorOptions(vendors);
      if (vendors.length > 0) {
        setValue("vendorCode", vendors[0]);
        setCustVendor({ vendorCode: vendors[0] });
      }
    },
    onError,
  });
  useSKUOptions({
    customerCode,
    onSuccess: (data) => setSKUOptions(data.data.data.map((x) => x.skuCode)),
    onError,
  });

  const infoProps = {
    values: {
      customerCode,
      defRecvWh,
      defRecvZone,
      defRecvLocation,
      defPutawayWh,
      defPutawayZone,
      defPutawayLocation,
      defTRPutawayWh,
      defTRPutawayZone,
      defTRPutawayLocation,
      defShipWh,
      defShipZone,
      defShipLocation,
    },
    vendorOptions,
    skuOptions,
    control,
    errors,
    formRender,
    setValue,
  };
  const createStatus = createVendorSKUMutation.status === "loading"
  const onSubmit = (data) => {
    window.scrollTo(0, 0);
    createVendorSKUMutation.mutate(data, {
      onSuccess: () => {
        if (!auto) {
          navigate("/setup/sku/retail-barcode/add", { state: { skuCode: data.skuCode, 
            vendorCode:data.vendorCode,customerCode:data.customerCode } });
        } else {
          reset();
          setFormValues({ ...formValues, ...custVendor, ...custSetting });
        }
      },
    });
  };
  return (
    <Layout lg={12}>
      {alert}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <VendorSKUInfo {...infoProps} />
        <SubmitButton label="submit" disabled={createStatus} url={-1} />
      </form>
    </Layout>
  );
}

export default AddVendorSKU;
